import { useState } from 'react'
import { format } from 'date-fns'
import { useDocuments, useDeleteDocument, useDocumentUrl } from '@/hooks/useDocuments'
import type { Document } from '@/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  FileText,
  MoreVertical,
  Eye,
  Download,
  Trash2,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { toast } from 'sonner'

interface DocumentListProps {
  submissionId: string
  onDocumentSelect?: (document: Document) => void
  selectedDocumentId?: string
}

const statusConfig: Record<
  string,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  pending: { label: 'Pending', variant: 'secondary' },
  processing: { label: 'Processing', variant: 'default' },
  completed: { label: 'Processed', variant: 'outline' },
  failed: { label: 'Failed', variant: 'destructive' }
}

function DocumentItem({
  document,
  isSelected,
  onSelect,
  onDelete
}: {
  document: Document
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
}) {
  const { data: signedUrl, isLoading: urlLoading } = useDocumentUrl(document.storage_path)
  const status = statusConfig[document.status] || statusConfig.pending

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleView = () => {
    if (signedUrl) {
      window.open(signedUrl, '_blank')
    }
  }

  const handleDownload = async () => {
    if (signedUrl) {
      const link = window.document.createElement('a')
      link.href = signedUrl
      link.download = document.file_name
      link.click()
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors cursor-pointer',
        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      <FileText className="h-8 w-8 flex-shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium">{document.file_name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatFileSize(document.file_size)}</span>
          <span>-</span>
          <span>{format(new Date(document.created_at), 'MMM d, yyyy')}</span>
        </div>
        {document.error_message && (
          <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3 w-3" />
            {document.error_message}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleView} disabled={urlLoading}>
              <Eye className="mr-2 h-4 w-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDownload} disabled={urlLoading}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export function DocumentList({
  submissionId,
  onDocumentSelect,
  selectedDocumentId
}: DocumentListProps) {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { data: documents, isLoading, error } = useDocuments(submissionId)
  const deleteMutation = useDeleteDocument()

  const handleDelete = async () => {
    if (!deleteId) return

    try {
      await deleteMutation.mutateAsync(deleteId)
      toast.success('Document deleted')
      setDeleteId(null)
    } catch (err) {
      toast.error('Failed to delete document')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertCircle className="mb-2 h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Failed to load documents</p>
      </div>
    )
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <FileText className="mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No documents uploaded</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((document) => (
          <DocumentItem
            key={document.id}
            document={document}
            isSelected={document.id === selectedDocumentId}
            onSelect={() => onDocumentSelect?.(document)}
            onDelete={() => setDeleteId(document.id)}
          />
        ))}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              document and all extracted data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
